// @ts-strict-ignore
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  BankAccountPaymentInstrument,
  Contact,
  ContactPaymentInstrument,
  CryptoContactInstrument,
  InstrumentTypes,
  WalletContactInstrumentEligibility,
} from '@node-space/types'
import { ActionListItem, Avatar } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import {
  getContactName,
  getIneligibleBankAccountMessage,
} from 'pages/Wallets/components/FiatSend/utils'
import { ContactInstrumentsListProps } from './ContactInstrumentsListV1'
import { ContactOrPaymentInstrumentList } from './ContactOrPaymentInstrumentList'

interface BankAccountItemProps {
  index: number
  contactName: string
  ineligibleBankAccountMessage?: string
  paymentInstrument: BankAccountPaymentInstrument | CryptoContactInstrument
  contact: Contact
  eligibility?: WalletContactInstrumentEligibility[]
  onSelect: (item: ContactPaymentInstrument) => void
}

interface CryptoItemProps {
  index: number
  contactName: string
  paymentInstrument: BankAccountPaymentInstrument | CryptoContactInstrument
  contact: Contact
  eligibility?: WalletContactInstrumentEligibility[]
  onSelect: (item: ContactPaymentInstrument) => void
}

const BankAccountPaymentInstrumentItem = ({
  paymentInstrument,
  contact,
  eligibility,
  index,
  contactName,
  ineligibleBankAccountMessage,
  onSelect,
}: BankAccountItemProps) => {
  const instrument = paymentInstrument as BankAccountPaymentInstrument
  const { accountNumber, bank } = instrument || {}
  const { identificationCode, swiftCode } = bank || {}

  const secondaryText = !ineligibleBankAccountMessage
    ? {
        BodyPre: instrument?.description,
      }
    : { BodyPre: ineligibleBankAccountMessage }

  const rightPrimaryText =
    (accountNumber?.length || 0) > 20 ? ellipseMiddleText(accountNumber || '', 6, 8) : accountNumber
  const rightSecondaryText =
    ((identificationCode || swiftCode || '').length || 0) > 20
      ? ellipseMiddleText(identificationCode || swiftCode || '', 6, 8)
      : identificationCode || swiftCode || ''

  return (
    <ActionListItem
      key={instrument?.id}
      id={instrument?.id}
      testId={`contacts-step-list-item-${index}`}
      {...(!ineligibleBankAccountMessage && {
        onItemClick: () => onSelect({ paymentInstrument, contact, eligibility }),
      })}
      disabled={!!ineligibleBankAccountMessage}
      title={contactName}
      Avatar={<Avatar size="default" type="currency" name={instrument?.currency?.toLowerCase()} />}
      {...secondaryText}
      RightPrimaryText={rightPrimaryText}
      RightSecondaryText={rightSecondaryText}
    />
  )
}

const CryptoPaymentInstrumentItem = ({
  paymentInstrument,
  contact,
  eligibility,
  index,
  contactName,
  onSelect,
}: CryptoItemProps) => {
  const instrument = paymentInstrument as CryptoContactInstrument

  return (
    <ActionListItem
      key={contact?.id}
      id={contact?.id}
      title={contactName}
      testId={`contacts-step-list-item-${index}`}
      Avatar={<Avatar size="default" type="currency" name={instrument?.currency?.toLowerCase()} />}
      BodyPre={<p className="text-label min-w-[0px] line-clamp-1">{instrument?.description}</p>}
      onItemClick={() => onSelect({ paymentInstrument, contact, eligibility })}
      RightPrimaryText={ellipseMiddleText(instrument?.address || '', 6, 6, '*')}
    />
  )
}

export const ContactInstrumentsListV2 = ({
  contacts,
  isFetchingNextPage,
  hasLoadedMore,
  hasNextPage,
  handleContactSelect,
  setHasLoadedMore,
  fetchNextPage,
}: ContactInstrumentsListProps) => {
  const { t } = useTranslation()

  return (
    <ContactOrPaymentInstrumentList
      list={contacts}
      isFetchingNextPage={isFetchingNextPage}
      hasLoadedMore={hasLoadedMore}
      hasNextPage={hasNextPage}
      setHasLoadedMore={setHasLoadedMore}
      fetchNextPage={fetchNextPage}
      getItemKey={item => item.paymentInstrument?.id || item?.contact?.id}
      listItemRenderer={(item, index) => {
        const { paymentInstrument, contact, eligibility } = item
        const isBankAccount = paymentInstrument?.type === InstrumentTypes.BANK_ACCOUNT
        const contactName = getContactName(contact)
        const ineligibleBankAccountMessage = getIneligibleBankAccountMessage(
          t,
          paymentInstrument?.currency || '',
          eligibility
        )

        return isBankAccount ? (
          <BankAccountPaymentInstrumentItem
            paymentInstrument={paymentInstrument as BankAccountPaymentInstrument}
            contact={contact}
            eligibility={eligibility}
            index={index}
            contactName={contactName}
            ineligibleBankAccountMessage={ineligibleBankAccountMessage}
            onSelect={handleContactSelect}
          />
        ) : (
          <CryptoPaymentInstrumentItem
            paymentInstrument={paymentInstrument as CryptoContactInstrument}
            contact={contact}
            eligibility={eligibility}
            index={index}
            contactName={contactName}
            onSelect={handleContactSelect}
          />
        )
      }}
    />
  )
}
